import React, { useState } from 'react';

// Runs a Universal API custom-code snippet's before() and after() IN THE BROWSER against a context the
// author types, and shows what each left behind — above all ctx.custom, which is the only way to see the
// value (a blob name, say) the integration would be handed without sending a real request.
//
// THE SAME CONTRACT AS /opt/core/uapi-custom-code: a CommonJS module exporting before/after, one ctx
// object shared by both, ctx.respond() to answer without the backend, a returned { statusCode } from
// after() replacing the response. What differs is only what cannot exist in a browser — `require` of a
// Node builtin throws here, and says so, rather than pretending.

const clone = (v) => (v === undefined ? undefined : JSON.parse(JSON.stringify(v)));

const isResponse = (v) => v && typeof v === 'object' && typeof v.statusCode === 'number';

const compile = (code) => {
    const module = { exports: {} };
    const require = (name) => {
        throw new Error(`require('${name}') is not available in the browser test — it works on the gateway`);
    };
    // eslint-disable-next-line no-new-func
    new Function('module', 'exports', 'require', code)(module, module.exports, require);
    const hooks = (module.exports && module.exports.default) || module.exports || {};
    if (typeof hooks.before !== 'function' && typeof hooks.after !== 'function') {
        throw new Error('custom code must export before(ctx) and/or after(ctx)');
    }
    return hooks;
};

export const defaultHookTestContext = ({ propertyCode, binding } = {}) => JSON.stringify({
    request: {
        method: 'PUT',
        path: `/rsv/v1/hotels/${propertyCode || 'HOTEL'}/reservations/123456`,
        query: {},
        headers: {},
        body: { reservations: { reservation: [{ reservationIdList: [{ id: '123456', type: 'Reservation' }] }] } },
    },
    pathParams: { hotelId: propertyCode || 'HOTEL', reservationId: '123456' },
    operation: {
        route_path: '/rsv/v1', operation_path: '/hotels/{hotelId}/reservations/{reservationId}', method: 'PUT',
        module_path: '/rsv',
    },
    binding: binding || {},
    propertyCode: propertyCode || 'HOTEL',
    response: { statusCode: 200, body: { stored: true } },
}, null, 2);

const UapiHookTester = ({ code, context, onContextChange }) => {
    const [result, setResult] = useState(null);
    const [running, setRunning] = useState(false);

    const run = async () => {
        setRunning(true);
        const logs = [];
        let phase = 'compile';
        try {
            const input = JSON.parse(context || '{}');
            const hooks = compile(code || '');
            const ctx = {
                request: clone(input.request) || {},
                pathParams: input.pathParams || {},
                query: (input.request && input.request.query) || {},
                operation: input.operation || {},
                binding: input.binding || {},
                propertyCode: input.propertyCode || null,
                transactionId: 'browser-test',
                custom: {},
                response: null,
                log: (...parts) => logs.push(parts.join(' ')),
                respond: (statusCode, body, headers) => ({ statusCode, body, headers }),
            };

            let answered = null;
            if (hooks.before) {
                phase = 'before';
                const out = await hooks.before(ctx);
                if (isResponse(out)) answered = out;
            }
            const requestAfterBefore = clone(ctx.request);
            const customAfterBefore = clone(ctx.custom);

            let response = answered;
            if (!answered && hooks.after) {
                phase = 'after';
                ctx.response = clone(input.response) || { statusCode: 200, body: null };
                const out = await hooks.after(ctx);
                response = isResponse(out) ? out : ctx.response;
            }
            setResult({ ok: true, custom: customAfterBefore, request: requestAfterBefore, response, answered: !!answered, logs });
        } catch (e) {
            setResult({ ok: false, phase, error: e.message, logs });
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className="pst-test-body">
            <label className="uapi-cc-test-label" htmlFor="uapi-cc-test-ctx">
                Test context — <code>request</code>, <code>pathParams</code>, <code>operation</code>,{' '}
                <code>propertyCode</code>, and the backend <code>response</code> after() will see
            </label>
            <textarea
                id="uapi-cc-test-ctx"
                className="pst-code-editor"
                value={context}
                onChange={(e) => onContextChange(e.target.value)}
                spellCheck={false}
                rows={12}
            />
            <div className="uapi-cc-test-actions">
                <button type="button" className="btn btn-secondary btn-sm" onClick={run} disabled={running}>
                    {running ? 'Running…' : 'Run before → after'}
                </button>
            </div>

            {result && !result.ok && (
                <div className="uapi-cc-test-out is-error">
                    <strong>Threw in {result.phase}:</strong> {result.error}
                    <p>On the gateway this is a 500 to the caller naming this snippet.</p>
                </div>
            )}
            {result && result.ok && (
                <div className="uapi-cc-test-out">
                    <div className="uapi-cc-test-block">
                        <span>ctx.custom — handed to the integration as <code>this.custom</code></span>
                        <pre>{JSON.stringify(result.custom, null, 2)}</pre>
                    </div>
                    <div className="uapi-cc-test-block">
                        <span>request the integration receives</span>
                        <pre>{JSON.stringify(result.request, null, 2)}</pre>
                    </div>
                    <div className="uapi-cc-test-block">
                        <span>{result.answered ? 'answered by before() — the backend is NOT called' : 'response after after()'}</span>
                        <pre>{JSON.stringify(result.response, null, 2)}</pre>
                    </div>
                    {result.logs.length > 0 && (
                        <div className="uapi-cc-test-block">
                            <span>ctx.log</span>
                            <pre>{result.logs.join('\n')}</pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default UapiHookTester;
